<template>
    <component
        :is="tag"
        data-testid="card-item"
        :class="[
            'card flex bg-white px-4 min-h-12 min-w-0',
            {
                'rounded-lg': !noRounded,
                'flex-row justify-between gap-x-2 py-2': !stackMode,
                'flex-col gap-y-3 py-3': stackMode,
                'cursor-pointer select-none hover:bg-blue-100 active:bg-blue-300 hover:ring-1 active:ring-1 ring-surface ring-inset':
                    clickable,
            },
        ]"
    >
        <div
            :class="[
                'card-container flex grow min-w-0',
                { 'items-center': !stackMode },
                useSmallText ? 'space-x-3' : 'space-x-4',
            ]"
        >
            <InputCheckbox
                v-if="hasCheckbox && !stackMode"
                :disabled="checkboxDisabled"
                :model-value="checked"
                @change="checkboxChanged($event)"
                @click.stop
            />

            <div
                v-if="hasImage"
                class="image-area min-w-fit"
            >
                <!-- @slot A more flexiable image area that allows to pass custom image or icon. -->
                <slot name="imageArea">
                    <AssetCdnImage
                        :src="imgSrc"
                        :alt="imgAlt"
                        :width="smallImage ? 40 : 56"
                        :height="smallImage ? 40 : 56"
                        object-fit="cover"
                        :class="[
                            'rounded-lg',
                            smallImage ? 'w-10 h-10' : 'w-14 h-14',
                        ]"
                    />
                </slot>
            </div>

            <div
                :class="[
                    'content-container flex grow min-w-0',
                    narrowMode ? 'items-center gap-x-2' : 'flex-col justify-center',
                ]"
            >
                <!-- title row -->
                <div
                    :class="[
                        'flex shrink min-w-0 items-start',
                        {
                            // somehow this div does not match the line-height, so I'm applying a max height
                            [titleHeightClass]: !hasContent && hasSecondaryActionSlot && defaultMode,
                        },
                    ]"
                >
                    <component
                        :is="titleTag"
                        :class="[
                            'title-area',
                            useSmallText ? 'font-body-3-medium' : 'font-body-2-medium',
                            hasContent || narrowMode ? 'truncate' : 'line-clamp-2',
                            {
                                // if no content, but a secondary slot is present, the title should also fill the content area
                                '-mb-[1em]': !hasContent && hasSecondaryActionSlot && defaultMode,
                            },
                        ]"
                    >
                        <!-- @slot A more flexible title that replaces the title prop. -->
                        <slot name="titleArea">
                            {{ title }}
                        </slot>
                    </component>

                    <div
                        v-if="hasTitleAddonSlot"
                        :class="[
                            'title-addon-area flex pl-2 place-self-start items-center',
                            {
                                'ml-auto': stackMode,
                            },
                            titleHeightClass,
                        ]"
                    >
                        <!-- @slot title addon located behind the title -->
                        <slot name="titleAddonArea" />
                    </div>

                    <div
                        v-if="hasPrimaryActionSlot && defaultMode"
                        :class="[
                            'primary-action-area flex ml-auto pl-2 place-self-start items-center whitespace-nowrap',
                            titleHeightClass,
                        ]"
                    >
                        <!-- @slot The primary action area which is centered on the first line of the title in default mode -->
                        <slot name="primaryActionArea"></slot>
                    </div>
                </div>

                <!-- content row -->
                <div
                    v-if="hasContent || (hasSecondaryActionSlot && defaultMode)"
                    class="flex shrink min-w-0"
                >
                    <div
                        v-if="hasContent || defaultMode"
                        :class="[
                            'content-area flex-grow',
                            {
                                truncate: narrowMode,
                                'font-body-3-regular': !(useSmallText !== narrowMode),
                                'font-body-2-regular': !useSmallText && narrowMode,
                                'font-caption-regular': useSmallText && !narrowMode,
                            },
                        ]"
                    >
                        <!-- @slot slot for content that should appear underneath the title. It contains the subtitle by default -->
                        <slot name="contentArea">
                            {{ subtitle }}
                        </slot>
                    </div>

                    <div
                        v-if="hasSecondaryActionSlot && defaultMode"
                        :class="[
                            'secondary-action-area flex place-self-end ml-auto items-center whitespace-nowrap',
                            // if no content is defined, the tags should be based on the title line height
                            hasContent ? contentHeightClass : titleHeightClass,
                        ]"
                    >
                        <!-- @slot The secondary action area which is centered on the last line of the content in default mode -->
                        <slot name="secondaryActionArea"></slot>
                    </div>
                </div>
            </div>
        </div>

        <!-- elements behind/below the content for non default mode -->
        <div
            v-if="!defaultMode && (hasPrimaryActionSlot || hasSecondaryActionSlot || (stackMode && hasCheckbox))"
            class="flex shrink-0 !leading-5 gap-x-2 items-center"
        >
            <InputCheckbox
                v-if="stackMode && hasCheckbox"
                :disabled="checkboxDisabled"
                :model-value="checked"
                @change="checkboxChanged($event)"
                @click.stop
            />

            <div
                v-if="hasPrimaryActionSlot"
                class="primary-action-area flex items-center whitespace-nowrap"
            >
                <slot name="primaryActionArea"></slot>
            </div>

            <div
                v-if="hasSecondaryActionSlot"
                :class="['secondary-action-area flex items-center whitespace-nowrap', { '!ml-auto': stackMode }]"
            >
                <slot name="secondaryActionArea"></slot>
            </div>
        </div>
    </component>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { type PropType } from 'vue';
import { slotIsUsed } from '@/helpers/dom';
import InputCheckbox from '@/components/InputCheckbox';
 
import Link from '@/components/Link';
import type { EnumToUnion } from '@/types/misc';
import { CardMode } from './Card';

/**
 * A generic card component that provides different layouts for an image, title, subtitle/content, and two action areas.
 *
 * The card component supports redirection behavior for native 'href' and vue-router through 'to' attributes.
 */
export default defineComponent({
    name: 'Card',
    components: {
        InputCheckbox,
    },
    props: {
        /**
         * The title that goes to the Card.
         */
        title: {
            type: String,
            default: undefined,
        },
        /**
         * The tag for the title to adjust the content based on the page structure.
         * @example 'h2'
         */
        titleTag: {
            type: String,
            default: 'div',
        },
        /**
         * The subtitle that goes under the Card's title.
         */
        subtitle: {
            type: String,
            default: undefined,
        },
        /**
         * A URL to an image that appears on the left of the title.
         */
        imgSrc: {
            type: String,
            default: undefined,
        },
        /**
         * An alternative text as a replacement to the image.
         */
        imgAlt: {
            type: String,
            default: '',
        },
        /**
         * Control the state of the checkbox.
         * Adds a checkbox when specified
         */
        checked: {
            type: Boolean as PropType<boolean | undefined>,
            default: undefined,
        },
        /**
         * Control the disability of the checkbox.
         */
        checkboxDisabled: {
            type: Boolean,
            default: false,
        },
        /**
         * The arrangement of the image, the title, the subtitle, and the action areas.
         * Default mode puts the action areas to the right side of the titles.
         * Narrow mode puts the subtitle in front of the title.
         * Stack mode puts the action areas under the titles.
         */
        mode: {
            type: String as PropType<EnumToUnion<CardMode>>,
            default: CardMode.Default,
        },
        /**
         * Applies styling indicating clickiness
         */
        clickable: {
            type: Boolean,
            default: false,
        },
        /**
         * Renders smaller text elements
         */
        useSmallText: {
            type: Boolean,
            default: false,
        },
        /**
         * Renders smaller image.
         * The image is already small in narrow mode.
         */
        useSmallImg: {
            type: Boolean,
            default: false,
        },
        /**
         * Removes rounded corners
         */
        noRounded: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:checked'],
    computed: {
        tag(): string | typeof Link {
            if (this.$attrs.to || this.$attrs.href) {
                return Link;
            }
            return 'article';
        },
        defaultMode(): boolean {
            return this.mode === CardMode.Default;
        },
        narrowMode(): boolean {
            return this.mode === CardMode.Narrow;
        },
        stackMode(): boolean {
            return this.mode === CardMode.Stack;
        },
        hasImage() {
            return !!this.imgSrc || slotIsUsed(this.$slots.imageArea);
        },
        smallImage(): boolean {
            return this.narrowMode || this.useSmallImg;
        },
        hasContent() {
            return !!(this.subtitle || slotIsUsed(this.$slots.contentArea));
        },
        hasCheckbox() {
            return this.checked !== undefined;
        },
        hasPrimaryActionSlot() {
            return slotIsUsed(this.$slots.primaryActionArea);
        },
        hasTitleAddonSlot() {
            return slotIsUsed(this.$slots.titleAddonArea);
        },
        hasSecondaryActionSlot() {
            return slotIsUsed(this.$slots.secondaryActionArea);
        },
        titleHeightClass() {
            return this.useSmallText ? 'h-5' : 'h-6';
        },
        contentHeightClass() {
            return this.useSmallText ? 'h-4' : 'h-5';
        },
    },
    methods: {
        checkboxChanged(state: boolean): void {
            this.$emit('update:checked', state);
        },
    },
});
</script>
